import { useEffect, useState } from "react";
import { sendPasswordResetEmail } from "firebase/auth";
import { KeyRound, LoaderCircle, Plus, ShieldCheck, ShieldOff } from "lucide-react";
import { Badge, Card, EmptyState, Modal, PageHeader } from "../components/ui";
import { useAdminAuth } from "../auth/AdminAuth";
import { auth } from "../firebase";
import { setAdminUser } from "../services/admin";
import type { AdminUser } from "../types";
import { dateTime } from "../utils";

export function AdminsPage({ admins }: { admins: AdminUser[] }) {
  const { role, user } = useAdminAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const canManage = role === "owner";
  const toggle = async (admin: AdminUser) => {
    setBusy(admin.id);
    try { await setAdminUser(admin.email, admin.role, !admin.active); } finally { setBusy(""); }
  };
  return <>
    <PageHeader title="Администраторски достъп" description="Дайте на клиента и екипа безопасен достъп без Firebase Console." action={canManage ? <button className="button primary" onClick={() => setOpen(true)}><Plus size={18} /> Добави администратор</button> : undefined} />
    <div className="security-note"><ShieldCheck size={22} /><div><strong>Защитено с роли и Firebase Authentication</strong><p>Всеки администратор влиза със собствен имейл. Всички промени се ограничават от защитни правила, а паролите никога не се виждат тук.</p></div></div>
    <Card>
      {admins.length === 0 ? <EmptyState title="Няма добавени администратори" body="Активирайте първия собственик или добавете профил." /> : <div className="admin-list">{admins.map((admin) => <article key={admin.id}><span className="avatar">{(admin.displayName?.[0] || admin.email[0]).toUpperCase()}</span><div className="grow"><strong>{admin.displayName || admin.email}</strong><small>{admin.email} · добавен {dateTime(admin.createdAt)}</small></div><Badge tone={admin.role === "owner" ? "purple" : admin.role === "support" ? "blue" : "neutral"}>{admin.role === "owner" ? "Собственик" : admin.role === "support" ? "Поддръжка" : "Мениджър"}</Badge><Badge tone={admin.active ? "green" : "red"}>{admin.active ? "Активен" : "Спрян"}</Badge>{canManage && admin.email !== user.email && <button className="button secondary small" disabled={busy === admin.id} onClick={() => toggle(admin)}>{admin.active ? <ShieldOff size={15} /> : <ShieldCheck size={15} />}{admin.active ? "Спри" : "Активирай"}</button>}</article>)}</div>}
    </Card>
    <AddAdmin open={open} onClose={() => setOpen(false)} />
  </>;
}

function AddAdmin({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("manager");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [complete, setComplete] = useState(false);
  useEffect(() => {
    if (open) {
      setComplete(false);
      setMessage("");
      setEmail("");
    }
  }, [open]);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      const result = await setAdminUser(email, role, true);
      if (result.created) {
        setComplete(true);
        try {
          await sendPasswordResetEmail(auth, email.trim());
          setMessage("Профилът е създаден. Изпратихме имейл за задаване на парола.");
        } catch {
          setMessage("Профилът е създаден, но имейлът за парола не бе изпратен. Използвайте „Забравена парола?“ от екрана за вход.");
        }
      } else {
        setEmail("");
        onClose();
      }
    } catch (reason) { setMessage(reason instanceof Error ? reason.message : "Профилът не бе добавен."); }
    finally { setBusy(false); }
  };
  return <Modal open={open} title="Добави администратор" subtitle="Ако няма профил, ще го създадем и ще изпратим имейл за парола." onClose={onClose}><form className="form-stack" onSubmit={submit}><label>Имейл<input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required disabled={complete} placeholder="client@example.com" /></label><label>Роля<select value={role} disabled={complete} onChange={(event) => setRole(event.target.value)}><option value="manager">Мениджър — пълен достъп до магазина</option><option value="support">Поддръжка — поръчки и клиенти</option><option value="owner">Собственик — включително достъпите</option></select></label>{message && <div className={`form-message ${complete ? "" : "error"}`}>{message}</div>}<div className="modal-actions">{complete ? <button type="button" className="button primary" onClick={() => { setComplete(false); setMessage(""); setEmail(""); onClose(); }}>Готово</button> : <><button type="button" className="button secondary" onClick={onClose}>Отказ</button><button className="button primary" disabled={busy}>{busy ? <LoaderCircle className="spin" size={17} /> : <KeyRound size={17} />} Добави достъп</button></>}</div></form></Modal>;
}
